import { aiChatService } from "../supabaseServices";
import { fetchWeather } from "../agents/weatherAgent";
import { generateMemeWithAI, showMemeTemplates } from "../agents/memeAgent";
import { giphyService } from "../agents/giphyAgent";
import { recipeService } from "../agents/recipeAgent";
import { youtubeService } from "../agents/youtubeAgent";
import { wikipediaService } from "../agents/wikipediaAgent";
import { pixabayService } from "../agents/pixabayAgent";
import { evaluateMathExpression } from "../agents/mathAgent";
import { generateQRCodeURL } from "../agents/qrCodeAgent";
import { generateColorPaletteWithAI } from "../agents/colorPaletteAgent";
import { searchWeb } from "../tavilyService";
import { newsAgent } from "../agents/newsAgent";
import { buildFlowChart } from "../agents/flowAgent";
import {
  gptOssCalculator,
  gptOssCalculatorWithExplanation,
  gptOssTranslator,
  gptOssCodeExplain,
} from "../agents/gptOssAgentService";

export interface AgenticTask {
  id: string;
  agentName: string;
  action: string;
  parameters: any;
  status: 'pending' | 'executing' | 'completed' | 'failed';
  result?: any;
  error?: string;
  displayName?: string;
}

export interface AgenticResponse {
  tasks: AgenticTask[];
  summary?: string;
  isSequential: boolean;
  mode?: 'agent_tasks' | 'chatbot_response';
  response?: string;
}

interface AgentDefinition {
  name: string;
  aliases: string[];
  description: string;
  requiredParams: string[];
  optionalParams?: string[];
  examples: string[];
}

const AGENT_DEFINITIONS: AgentDefinition[] = [
  {
    name: "weather",
    aliases: ["weather", "temperature", "forecast", "climate"],
    description: "Get weather information for any location",
    requiredParams: ["location"],
    examples: ["weather in Delhi", "what's the temperature in New York", "forecast for London"]
  },
  {
    name: "meme",
    aliases: ["meme", "funny", "joke image"],
    description: "Generate memes with custom text",
    requiredParams: ["text or topic"],
    examples: ["create a meme about coding", "make a funny meme about Monday", "generate meme"]
  },
  {
    name: "calculator",
    aliases: ["calculate", "math", "compute", "solve"],
    description: "Perform mathematical calculations",
    requiredParams: ["expression"],
    examples: ["calculate 25 * 4", "solve 2^10", "what is 15% of 200"]
  },
  {
    name: "translator",
    aliases: ["translate", "translation", "convert language"],
    description: "Translate text between languages",
    requiredParams: ["text", "target_language"],
    optionalParams: ["source_language"],
    examples: ["translate hello to Spanish", "convert 'bonjour' to English"]
  },
  {
    name: "web-search",
    aliases: ["search", "google", "find", "look up", "web"],
    description: "Search the web using Tavily API",
    requiredParams: ["query"],
    examples: ["search for latest AI news", "find information about quantum computing"]
  },
  {
    name: "news",
    aliases: ["news", "headlines", "current events", "latest news"],
    description: "Get latest news using Tavily",
    requiredParams: ["topic or category"],
    examples: ["get tech news", "latest headlines", "news about AI"]
  },
  {
    name: "image-search",
    aliases: ["image", "photo", "picture", "pixabay", "images"],
    description: "Search for images using Pixabay",
    requiredParams: ["query"],
    examples: ["find images of cats", "search photos of sunset", "get pictures of mountains"]
  },
  {
    name: "youtube",
    aliases: ["youtube", "video", "yt"],
    description: "Search YouTube videos",
    requiredParams: ["query"],
    examples: ["search youtube for cooking tutorials", "find videos about React"]
  },
  {
    name: "wikipedia",
    aliases: ["wikipedia", "wiki", "encyclopedia"],
    description: "Search Wikipedia articles",
    requiredParams: ["query"],
    examples: ["wikipedia article on Einstein", "search wiki for quantum physics"]
  },
  {
    name: "recipe",
    aliases: ["recipe", "cooking", "food", "dish"],
    description: "Find recipes and cooking instructions",
    requiredParams: ["dish or ingredients"],
    examples: ["recipe for pasta", "how to make chocolate cake"]
  },
  {
    name: "gif",
    aliases: ["gif", "giphy", "animated"],
    description: "Search for GIFs",
    requiredParams: ["query"],
    examples: ["find funny cat gifs", "search for happy birthday gif"]
  },
  {
    name: "qr-code",
    aliases: ["qr", "qr code", "qrcode", "barcode"],
    description: "Generate QR codes",
    requiredParams: ["text or url"],
    examples: ["create QR code for https://example.com", "generate QR for 'Hello World'"]
  },
  {
    name: "color-palette",
    aliases: ["color", "palette", "colors", "theme"],
    description: "Generate color palettes",
    requiredParams: ["theme or mood"],
    examples: ["generate ocean color palette", "create warm colors", "sunset theme colors"]
  },
  {
    name: "code",
    aliases: ["code", "programming", "explain code", "debug"],
    description: "Explain or help with code",
    requiredParams: ["code or programming question"],
    examples: ["explain this Python function", "help me debug JavaScript code"]
  },
  {
    name: "flow",
    aliases: ["flow", "flowchart", "diagram", "chart", "process flow", "react flow", "reactflow"],
    description: "Builds interactive flowcharts and diagrams",
    requiredParams: ["instruction or steps"],
    examples: [
      "create a signup flow with validation and success page",
      "flowchart for e-commerce order lifecycle",
      "diagram of microservice request routing"
    ]
  }
];

const LARA_SYSTEM_PROMPT = `You are LARA (Language-Aware Resource Assistant), an advanced AI orchestrator and general assistant created by the OREOG AI team. 

## About LARA:
I'm LARA, your intelligent assistant capable of both executing specific tasks through specialized agents and engaging in general conversations. I was designed to understand complex multi-step requests and coordinate various AI agents to help you accomplish your goals efficiently. When you need specific tasks done, I orchestrate the right tools. When you just want to chat, I'm here as your friendly AI companion.

## My Capabilities:
1. **Multi-Agent Orchestration**: I can coordinate multiple specialized agents to handle complex requests
2. **General AI Assistant**: I can engage in conversations, answer questions, provide explanations, and help with various topics
3. **Task Planning**: I analyze your requests and determine the best approach to help you

Your task is to:
1. Analyze the user's input to identify if they want specific agent tasks OR general conversation
2. For agent tasks: Map each task to the appropriate agent(s), extract parameters, determine execution order
3. For general conversation: Respond naturally as an AI assistant with no agent tasks
4. If unsure whether user wants agents or conversation, default to general conversation

Available agents and their capabilities:
${AGENT_DEFINITIONS.map(agent =>
  `- ${agent.name}: ${agent.description}
   Aliases: ${agent.aliases.join(', ')}
   Required params: ${agent.requiredParams.join(', ') || 'none'}
   Optional params: ${agent.optionalParams?.join(', ') || 'none'}`
).join('\n')}

IMPORTANT RULES:
1. For news requests, ALWAYS use the news agent (which uses Tavily internally) - DO NOT use web-search for news
2. For image searches, ALWAYS use image-search agent (which uses Pixabay)
3. For web searches (not news), use web-search agent (which uses Tavily)
4. If user asks for multiple tasks, identify ALL of them
5. Tasks that depend on each other should be marked as sequential
6. Independent tasks can run in parallel
7. For color palettes, NEVER generate more than 9 colors maximum
8. **NEW**: If the user is asking general questions, wants to chat, or asks about LARA, return NO agent tasks (empty tasks array) and use "chatbot_response" mode

For AGENT TASKS, return this JSON format:
{
  "tasks": [
    {
      "agentName": "agent-name-here",
      "action": "specific action description",
      "parameters": {
        "param1": "value1",
        "param2": "value2"
      },
      "displayName": "Human-friendly name for UI display"
    }
  ],
  "isSequential": true/false,
  "reasoning": "Brief explanation of your task plan"
}

For GENERAL CONVERSATION, return this JSON format:
{
  "tasks": [],
  "isSequential": false,
  "mode": "chatbot_response",
  "response": "Your conversational response here",
  "reasoning": "This is a general conversation request"
}

Examples:
User: "Generate a meme about AI and also tell me the weather in Delhi"
Response: {
  "tasks": [
    {
      "agentName": "meme",
      "action": "generate_meme",
      "parameters": {"topic": "AI", "text": "about AI"},
      "displayName": "Meme Generator"
    },
    {
      "agentName": "weather", 
      "action": "get_weather",
      "parameters": {"location": "Delhi"},
      "displayName": "Weather Agent"
    }
  ],
  "isSequential": false,
  "reasoning": "Two independent tasks: meme generation and weather check"
}`;

export class LARAOrchestrator {
  private static instance: LARAOrchestrator;

  private constructor() { }

  public static getInstance(): LARAOrchestrator {
    if (!LARAOrchestrator.instance) {
      LARAOrchestrator.instance = new LARAOrchestrator();
    }
    return LARAOrchestrator.instance;
  }

  async analyzeRequest(userInput: string): Promise<AgenticResponse> {
    try {
      // Call GPT-OSS to analyze the request
      const response = await aiChatService(
        "gpt-oss",
        [
          { role: "system", content: LARA_SYSTEM_PROMPT },
          { role: "user", content: userInput }
        ],
        0.3
      );

      // Extract content from response
      const responseText = typeof response === "object" && response?.content
        ? response.content
        : typeof response === "string"
          ? response
          : String(response);

      // Parse the JSON response
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("Failed to parse LARA response");
      }

      const plan = JSON.parse(jsonMatch[0]);

      // Create tasks with unique IDs
      const tasks: AgenticTask[] = plan.tasks.map((task: any, index: number) => ({
        id: `task-${Date.now()}-${index}`,
        agentName: task.agentName,
        action: task.action,
        parameters: task.parameters,
        status: 'pending' as const,
        displayName: task.displayName || task.agentName
      }));

      // Check if this is a chatbot response mode
      if (plan.mode === 'chatbot_response' || tasks.length === 0) {
        return {
          tasks: [],
          summary: plan.reasoning || "General conversation response",
          isSequential: false,
          mode: 'chatbot_response',
          response: plan.response || "I'm LARA, your AI assistant. How can I help you today?"
        };
      }

      return {
        tasks,
        summary: plan.reasoning,
        isSequential: plan.isSequential !== false,
        mode: 'agent_tasks'
      };
    } catch (error) {
      console.error("Error analyzing request:", error);
      // Fallback to single task execution
      return {
        tasks: [],
        summary: "Failed to analyze request",
        isSequential: true
      };
    }
  }

  async executeTask(task: AgenticTask, onProgress?: (status: string) => void): Promise<AgenticTask> {
    task.status = 'executing';

    if (onProgress) {
      onProgress(`Executing ${task.displayName || task.agentName}...`);
    }

    try {
      let result: any;

      switch (task.agentName) {
        case 'weather':
          const weatherData = await fetchWeather(task.parameters.location);
          result = {
            type: 'weather',
            data: weatherData,
            format: 'card'
          };
          break;

        case 'meme':
          if (task.parameters.showTemplates) {
            result = await showMemeTemplates();
          } else {
            result = await generateMemeWithAI(
              task.parameters.text || task.parameters.topic,
              task.parameters.isDarkMode
            );
          }
          break;

        case 'calculator':
          const calcResultWithExplanation = await gptOssCalculatorWithExplanation(task.parameters.expression);
          result = {
            type: 'calculator',
            data: {
              expression: task.parameters.expression,
              result: calcResultWithExplanation.result,
              explanation: calcResultWithExplanation.explanation
            },
            format: 'text'
          };
          break;

        case 'translator':
          const transResult = await gptOssTranslator(
            task.parameters.text,
            task.parameters.target_language
          );
          result = {
            type: 'translator',
            data: transResult,
            format: 'text'
          };
          break;

        case 'web-search':
          const searchResults = await searchWeb(task.parameters.query);
          result = {
            type: 'web-search',
            data: searchResults,
            format: 'search-results'
          };
          break;

        case 'news':
          const query = task.parameters.topic || task.parameters.category || task.parameters.query || 'latest news';
          const newsData = await newsAgent(query);
          result = {
            type: 'news',
            data: newsData,
            format: 'news-cards'
          };
          break;

        case 'image-search':
          const images = await pixabayService(task.parameters.query);
          result = {
            type: 'image-search',
            data: images,
            format: 'image-grid'
          };
          break;

        case 'youtube':
          const videos = await youtubeService(task.parameters.query);
          result = {
            type: 'youtube',
            data: videos,
            format: 'video-list'
          };
          break;

        case 'wikipedia':
          const wikiData = await wikipediaService(task.parameters.query);
          result = {
            type: 'wikipedia',
            data: wikiData,
            format: 'article'
          };
          break;

        case 'recipe':
          const recipes = await recipeService(task.parameters.dish || task.parameters.query);
          result = {
            type: 'recipe',
            data: recipes,
            format: 'recipe-cards'
          };
          break;

        case 'gif':
          const gifs = await giphyService(task.parameters.query);
          result = {
            type: 'gif',
            data: gifs,
            format: 'gif-grid'
          };
          break;

        case 'qr-code':
          const qrUrl = await generateQRCodeURL(task.parameters.text || task.parameters.url);
          result = {
            type: 'qr-code',
            data: { url: qrUrl, text: task.parameters.text },
            format: 'qr-display'
          };
          break;

        case 'color-palette':
          const palette = await generateColorPaletteWithAI(
            task.parameters.theme || task.parameters.mood,
            false // isDarkMode handled by renderer
          );
          result = {
            type: 'color-palette',
            data: palette,
            format: 'component'
          };
          break;

        case 'code':
          const codeResult = await gptOssCodeExplain(
            task.parameters.code || task.parameters.question
          );
          result = {
            type: 'code',
            data: codeResult,
            format: 'text'
          };
          break;

        case 'flow': {
          const instruction = task.parameters.instruction || task.parameters.steps || task.parameters.query || task.parameters.text || '';
          const spec = await buildFlowChart(instruction);
          result = {
            type: 'flow',
            data: spec,
            format: 'component'
          };
          break;
        }

        default:
          throw new Error(`Unknown agent: ${task.agentName}`);
      }

      task.status = 'completed';
      task.result = result;
      return task;

    } catch (error) {
      task.status = 'failed';
      task.error = error instanceof Error ? error.message : 'Unknown error';
      return task;
    }
  }

  async executeTasks(
    tasks: AgenticTask[],
    isSequential: boolean = true,
    onProgress?: (taskId: string, status: string) => void
  ): Promise<AgenticTask[]> {
    if (isSequential) {
      // Execute tasks one by one
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        if (onProgress) {
          onProgress(task.id, `Starting ${task.displayName || task.agentName}...`);
        }
        await this.executeTask(task, (status) => {
          if (onProgress) onProgress(task.id, status);
        });
      }
    } else {
      // Execute tasks in parallel
      await Promise.all(
        tasks.map(task =>
          this.executeTask(task, (status) => {
            if (onProgress) onProgress(task.id, status);
          })
        )
      );
    }

    return tasks;
  }

  formatTaskResult(task: AgenticTask): any {
    if (task.status !== 'completed' || !task.result) {
      return {
        type: 'error',
        content: task.error || 'Task failed',
        // omit agentName for privacy
      };
    }

    // Return the result with proper formatting information
    return {
      ...task.result,
      taskId: task.id
    };
  }
}

export default LARAOrchestrator;
